import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ShieldOff } from 'lucide-react';
import { api } from '../../services/api';
import DetailSection from '../details/DetailSection';
import Kpi from '../ui/Kpi';
import StateBlock from '../ui/StateBlock';
import { isForbidden } from '../sales/shared';
import { formatCurrency, formatNumber } from '../../utils/format';
import ProfitPeriodPicker from './ProfitPeriodPicker';
import ServerFigures from './ServerFigures';
import PendingDecision from '../ui/PendingDecision';
import MissingCostWarning from '../MissingCostWarning';
import {
  defaultPeriod, employeeRows, projectPlanSettingHref, serverLine, hoursBarModel, periodKey, periodParams, profitOutlook, showNegativeProfitWarning,
  type BarTone, type ProfitPeriod, type ProjectProfit,
} from './profitabilityModel';

const money = (n: number | null | undefined) => (n == null ? '—' : formatCurrency(n));
const hoursText = (n: number | null | undefined) => (n == null ? '—' : formatNumber(n, 1));

const BAR_FILL: Record<BarTone, string> = { ok: 'bg-primary-600', warn: 'bg-warning-500', danger: 'bg-danger-600' };

/** The project's profitability, for who sees money; shared by the tab and the warning at the top of the page. */
export function useProjectProfit(projectId: string | undefined, period: ProfitPeriod, enabled = true) {
  const params = periodParams(period);
  return useQuery({
    queryKey: ['project-profit', projectId, periodKey(period)],
    queryFn: async () => (await api.getProjectProfit(projectId!, params ?? undefined)).data as ProjectProfit,
    enabled: enabled && !!projectId && !!params,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * P-28b (D-169, D-15, D-95) - "רווחיות" in the project page. Few numbers, each with a title;
 * colour only in the small hours bar and the warning. The hours bar is orange from 80% of the
 * plan and red above 100%.
 */
export default function ProjectProfitabilityTab({ projectId, canEdit = true }: { projectId: string; canEdit?: boolean }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<ProfitPeriod>(() => defaultPeriod('all'));
  const { data, isLoading, error, refetch, isFetching } = useProjectProfit(projectId, period);

  return (
    <div className="space-y-5" data-profit-tab="">
      <ProfitPeriodPicker value={period} onChange={setPeriod} allowAll />
      {isLoading ? (
        <StateBlock kind="loading" />
      ) : isForbidden(error) ? (
        <p className="flex items-start gap-2 text-sm text-text-2"><ShieldOff size={16} className="mt-0.5 shrink-0" aria-hidden />{t('profitability.project.noPermission')}</p>
      ) : !data ? (
        <StateBlock kind="error" error={error} title={t('profitability.project.loadError')} onRetry={() => refetch()} retrying={isFetching} />
      ) : (
        <ProjectProfitBody data={data} period={period} projectId={projectId} canEdit={canEdit} />
      )}
    </div>
  );
}

/** HN-1: "no plan" says where to set it - the project's edit form (`?edit=1`), where "שעות מתוכננות" is. */
function NoPlan({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  return (
    <p className="mt-3 text-sm text-text-2" data-no-plan="">
      {t('profitability.project.noPlan')}
      {canEdit && (
        <>
          {' '}
          <Link to={projectPlanSettingHref(projectId)} className="font-medium text-primary-700 underline" data-no-plan-link="">
            {t('profitability.project.noPlanLink')}
          </Link>
        </>
      )}
    </p>
  );
}

function ProjectProfitBody({ data, period, projectId, canEdit }: { data: ProjectProfit; period: ProfitPeriod; projectId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const bar = hoursBarModel(data.plan?.actual_hours, data.plan?.planned_hours);
  const outlook = profitOutlook(data.plan);
  const emp = employeeRows(data.by_employee);
  const th = 'px-4 py-2 text-start text-xs font-medium text-surface-600';
  const td = 'px-4 py-2 text-sm text-surface-900';
  const allTime = period.kind === 'all';

  return (
    <>
      {/* DQ-062 (D-109): employees without a cost make the cost (and the profit) here inaccurate. */}
      <div className="empty:hidden"><MissingCostWarning count={data.cost_warning?.employees_missing_cost} /></div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3" data-profit-kpis="">
        <Kpi label={t('profitability.project.price')} value={money(data.price)} />
        <Kpi label={allTime ? t('profitability.project.revenueToDate') : t('profitability.project.revenuePeriod')} value={money(allTime ? data.revenue_to_date : data.revenue)} />
        <Kpi label={t('profitability.cost')} value={money(data.cost)} />
        <Kpi label={t('profitability.profit')} value={money(data.profit)} tone={data.profit < 0 ? 'danger' : undefined} />
        <Kpi label={t('profitability.revenuePerHour')} value={money(data.revenue_per_hour)} />
        <Kpi label={t('profitability.profitPerHour')} value={money(data.profit_per_hour)} />
      </div>
      {/* P-34 (D-184): the server - what the customer pays, what it costs OTE, the profit. */}
      <ServerFigures line={serverLine(data)} />

      <div className="grid gap-3 lg:grid-cols-2">
        <section aria-labelledby="pp-hours" className="rounded-xl border border-surface-200 bg-surface-0 p-5">
          <h3 id="pp-hours" className="text-sm font-semibold text-text">{t('profitability.project.hoursVsPlan')}</h3>
          {bar.pct == null ? (
            <NoPlan projectId={projectId} canEdit={canEdit} />
          ) : (
            <>
              <p className="mt-2 text-sm tabular-nums text-text-2">
                {t('profitability.project.hoursLine', { actual: hoursText(data.plan?.actual_hours), planned: hoursText(data.plan?.planned_hours) })}
              </p>
              <div
                className="mt-2 h-2 rounded-full bg-surface-100"
                role="progressbar"
                aria-valuenow={Math.round(bar.pct)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('profitability.project.hoursVsPlan')}
                data-hours-bar={bar.tone}
              >
                <div className={`h-2 rounded-full ${BAR_FILL[bar.tone]}`} style={{ width: `${bar.fill}%` }} />
              </div>
              <p className="mt-1 text-xs tabular-nums text-text-2">
                {t(`profitability.project.barNote.${bar.tone}`, { pct: formatNumber(bar.pct) })}
              </p>
            </>
          )}
        </section>

        <section aria-labelledby="pp-outlook" className="rounded-xl border border-surface-200 bg-surface-0 p-5">
          <h3 id="pp-outlook" className="text-sm font-semibold text-text">{t('profitability.project.plannedVsExpected')}</h3>
          {outlook.planned == null && outlook.expected == null ? (
            <NoPlan projectId={projectId} canEdit={canEdit} />
          ) : (
            <dl className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <dt className="text-xs text-text-2">{t('profitability.project.plannedProfit')}</dt>
                <dd className="text-xl font-semibold tabular-nums text-text">{money(outlook.planned)}</dd>
              </div>
              <div>
                <dt className="text-xs text-text-2">{t('profitability.project.expectedProfit')}</dt>
                <dd className={`text-xl font-semibold tabular-nums ${outlook.expected != null && outlook.expected < 0 ? 'text-danger-700' : 'text-text'}`}>{money(outlook.expected)}</dd>
              </div>
            </dl>
          )}
        </section>
      </div>

      <DetailSection title={t('profitability.project.byEmployee')} flush>
        {emp.rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-text-2">{t('profitability.project.noHours')}</p>
        ) : (
          // BQ-078 (a): 7 columns for the OTE manager - the table scrolls inside its card, never the page (390 px).
          <div className="overflow-x-auto">
          <table className="w-full">
            <caption className="sr-only">{t('profitability.project.byEmployee')}</caption>
            <thead className="border-b border-surface-200">
              <tr>
                <th scope="col" className={th}>{t('profitability.employee')}</th>
                <th scope="col" className={`${th} text-end`}>{t('profitability.hours')}</th>
                <th scope="col" className={`${th} text-end`}>{t('profitability.billableHours')}</th>
                {emp.showCost && <th scope="col" className={`${th} text-end`}>{t('profitability.cost')} <PendingDecision id="PM-27" /></th>}
                {emp.showProfit && <th scope="col" className={`${th} text-end`}>{t('profitability.revenue')}</th>}
                {emp.showProfit && <th scope="col" className={`${th} text-end`}>{t('profitability.profit')}</th>}
                {emp.showProfit && <th scope="col" className={`${th} text-end`}>{t('profitability.profitPerHour')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {emp.rows.map((e) => (
                <tr key={e.user_id}>
                  <td className={td}>{e.name}</td>
                  <td className={`${td} text-end tabular-nums`}>{hoursText(e.hours)}</td>
                  <td className={`${td} text-end tabular-nums`}>{hoursText(e.billable_hours)}</td>
                  {emp.showCost && <td className={`${td} text-end tabular-nums`}>{money(e.cost)}</td>}
                  {emp.showProfit && <td className={`${td} text-end tabular-nums`}>{money(e.revenue)}</td>}
                  {emp.showProfit && <td className={`${td} text-end tabular-nums`}>{money(e.profit)}</td>}
                  {emp.showProfit && <td className={`${td} text-end tabular-nums`}>{money(e.profit_per_hour)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </DetailSection>
    </>
  );
}

/**
 * "רווח שלילי" at the top of the project page (D-169). Only for who sees money; T-46: a project
 * with no price (0) has no such warning. A 403 or an error shows nothing: it is a notice, not a page.
 */
export function NegativeProfitNotice({ projectId, enabled }: { projectId: string; enabled: boolean }) {
  const { t } = useTranslation();
  const { data } = useProjectProfit(projectId, defaultPeriod('all'), enabled);
  if (!showNegativeProfitWarning(data)) return null;
  return (
    <div role="status" data-negative-profit="" className="flex flex-wrap items-start gap-x-2 gap-y-1 rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-800">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
      <span>{t('profitability.project.negativeWarning', { cost: money(data!.cost), price: money(data!.price) })}</span>
      <Link to="?tab=profitability" className="font-medium underline underline-offset-2">{t('profitability.project.openTab')}</Link>
    </div>
  );
}
